import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiMessageSquare, FiSend, FiTrash2, FiUser, FiX } from 'react-icons/fi';
import { sendChatMessage, clearChatHistory, healthCheck } from '../../services/chatbotApi';
import { LocationContactLocation } from '../../services/contactApi';
import ChatLocationContactPanel from './ChatLocationContactPanel';
import { isCdSite } from '../../utils/siteConfig';

type IconComponent = React.ComponentType<{ className?: string }>;
const XIcon = FiX as IconComponent;
const MessageSquareIcon = FiMessageSquare as IconComponent;
const UserIcon = FiUser as IconComponent;
const TrashIcon = FiTrash2 as IconComponent;
const SendIcon = FiSend as IconComponent;
const MapPinIcon = FiMapPin as IconComponent;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const Chatbot: React.FC = () => {
  const { t } = useTranslation();
  const isCdWebsite = isCdSite();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContactLocation, setSelectedContactLocation] = useState<LocationContactLocation | null>(null);
  const [conversationId] = useState<string>(() => 
    `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      const clickedInsideWindow = chatWindowRef.current?.contains(target);
      const clickedToggleButton = toggleButtonRef.current?.contains(target);

      if (clickedInsideWindow || clickedToggleButton) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen]);

  // Health check on mount
  useEffect(() => {
    healthCheck().catch(() => {
      // Silently fail - API might not be running
    });
  }, []);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && messages.length === 0) {
      // Add welcome message when opening for the first time
      setMessages([{
        id: 'welcome',
        text: t('chatbot.welcomeMessage', 'Hello! How can I help you today?'),
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  };

  const handleSendMessage = async (e?: React.FormEvent, messageText?: string) => {
    if (e) {
      e.preventDefault();
    }
    
    const textToSend = messageText || inputValue.trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      id: `user_${Date.now()}`,
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await sendChatMessage(textToSend, conversationId);
      
      const botMessage: Message = {
        id: `bot_${Date.now()}`,
        text: response.response || t('chatbot.errorMessage', 'Sorry, I encountered an error. Please try again.'),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: t('chatbot.errorMessage', 'Sorry, I encountered an error. Please try again.'),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(undefined, suggestion);
  };

  const handleClearChat = async () => {
    try {
      await clearChatHistory(conversationId);
      setMessages([{
        id: 'welcome',
        text: t('chatbot.welcomeMessage', 'Hello! How can I help you today?'),
        sender: 'bot',
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Clear chat error:', error);
      // Still clear local messages even if API call fails
      setMessages([{
        id: 'welcome',
        text: t('chatbot.welcomeMessage', 'Hello! How can I help you today?'),
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Format message text with proper line breaks, lists, and paragraphs
  const formatMessageText = (text: string): React.ReactNode => {
    if (!text) return text;

    // Split by double newlines to create paragraphs
    const paragraphs = text.split(/\n\n+/);
    
    return (
      <div className="w-full">
        {paragraphs.map((paragraph, pIndex) => {
          // Check if paragraph contains bullet points
          const lines = paragraph.split('\n');
          const hasBullets = lines.some(line => 
            line.trim().startsWith('-') || 
            line.trim().startsWith('•') ||
            line.trim().match(/^\d+\./)
          );

          if (hasBullets) {
            // Format as list
            return (
              <ul key={pIndex} className="mb-3 list-disc pl-5 last:mb-0">
                {lines.map((line, lIndex) => {
                  const trimmed = line.trim();
                  if (trimmed.startsWith('-') || trimmed.startsWith('•') || trimmed.match(/^\d+\./)) {
                    // Remove bullet marker
                    const content = trimmed.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
                    return (
                      <li key={lIndex} className="mb-1.5 pl-1 last:mb-0">
                        {formatInlineText(content)}
                      </li>
                    );
                  } else if (trimmed) {
                    // Regular line in list context
                    return (
                      <li key={lIndex} className="mb-1.5 pl-1 last:mb-0">
                        {formatInlineText(trimmed)}
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            );
          } else {
            // Format as regular paragraph with line breaks
            return (
              <p key={pIndex} className="mb-3 leading-6 last:mb-0">
                {lines.map((line, lIndex) => (
                  <React.Fragment key={lIndex}>
                    {formatInlineText(line)}
                    {lIndex < lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            );
          }
        })}
      </div>
    );
  };

  // Format inline text (URLs, emails, phone numbers, etc.)
  const formatInlineText = (text: string): React.ReactNode => {
    if (!text) return text;

    const parts: React.ReactNode[] = [];
    let keyCounter = 0;

    // URL pattern (https:// or http://)
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    // Email pattern
    const emailPattern = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    // Phone pattern (supports various formats)
    const phonePattern = /(\+?\d{1,4}[\s-]?\(?\d{1,4}\)?[\s-]?\d{1,4}[\s-]?\d{1,9})/g;

    // Find all matches with their positions
    const matches: Array<{ type: 'url' | 'email' | 'phone'; match: RegExpMatchArray; index: number }> = [];

    let match;
    while ((match = urlPattern.exec(text)) !== null) {
      matches.push({ type: 'url', match, index: match.index });
    }
    while ((match = emailPattern.exec(text)) !== null) {
      matches.push({ type: 'email', match, index: match.index });
    }
    while ((match = phonePattern.exec(text)) !== null) {
      matches.push({ type: 'phone', match, index: match.index });
    }

    // Sort matches by index
    matches.sort((a, b) => a.index - b.index);

    // Remove overlapping matches (prioritize URLs > emails > phones)
    const filteredMatches: typeof matches = [];
    let lastEnd = 0;
    for (const m of matches) {
      const start = m.index;
      const end = start + m.match[0].length;
      
      // Skip if overlaps with previous match
      if (start < lastEnd) {
        // If it's a URL and overlaps, replace the previous match
        if (m.type === 'url' && filteredMatches.length > 0) {
          const last = filteredMatches[filteredMatches.length - 1];
          const lastMatchEnd = last.index + last.match[0].length;
          if (start < lastMatchEnd) {
            filteredMatches.pop();
            filteredMatches.push(m);
            lastEnd = end;
            continue;
          }
        }
        continue;
      }
      
      filteredMatches.push(m);
      lastEnd = end;
    }

    // Build parts array
    let lastIndex = 0;
    for (const { type, match, index } of filteredMatches) {
      // Add text before match
      if (index > lastIndex) {
        parts.push(text.substring(lastIndex, index));
      }

      // Add formatted link
      const value = match[0];
      let href = value;
      
      if (type === 'url') {
        href = value;
      } else if (type === 'email') {
        href = `mailto:${value}`;
      } else if (type === 'phone') {
        href = `tel:${value.replace(/[\s-()]/g, '')}`;
      }

      parts.push(
        <a
          key={`link-${keyCounter++}`}
          href={href}
          target={type === 'url' ? '_blank' : undefined}
          rel={type === 'url' ? 'noopener noreferrer' : undefined}
          className="underline decoration-current/40 underline-offset-2 break-all hover:decoration-current"
        >
          {value}
        </a>
      );

      lastIndex = index + value.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        ref={toggleButtonRef}
        type="button"
        className={`fixed bottom-20 right-4 z-[1000] flex h-12 w-12 items-center justify-center rounded-2xl border border-teal-100 bg-white text-teal-600 shadow-[0_10px_24px_rgba(13,148,136,0.2)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-50 hover:shadow-[0_14px_30px_rgba(13,148,136,0.28)] sm:bottom-24 sm:right-6 sm:h-14 sm:w-14 lg:bottom-28 lg:right-6 ${isOpen ? 'ring-2 ring-teal-200' : ''}`}
        onClick={handleToggleChat}
        aria-label={t('chatbot.toggleButton', 'Toggle chat')}
      >
        <div className="flex items-center justify-center text-blue-400">
          {isOpen ? (
            <XIcon className="h-6 w-6 sm:h-7 sm:w-7" />
          ) : (
            <MessageSquareIcon className="h-6 w-6 sm:h-7 sm:w-7" />
          )}
        </div>

      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          ref={chatWindowRef}
          data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
          className="fixed bottom-20 right-4 z-[1001] flex h-[min(82dvh,36rem)] min-h-0 w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-teal-100 bg-white shadow-2xl sm:bottom-24 sm:right-24 sm:h-[34rem] sm:w-[24rem] lg:bottom-24 lg:right-24 lg:h-[32rem] lg:w-[23rem]"
        >
          <div className="bg-teal-600 text-white">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <UserIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold leading-tight">{t('chatbot.title', 'Chat Support')}</h3>
                  <p className="mt-0.5 truncate text-xs text-white/80">{t('chatbot.subtitle', 'We\'re here to help')}</p>
                </div>
              </div>
              <button
                type="button"
                className="ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 transition hover:bg-white/30"
                onClick={handleClearChat}
                aria-label={t('chatbot.clearChat', 'Clear chat')}
                title={t('chatbot.clearChat', 'Clear chat')}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>

            {isCdWebsite && !selectedContactLocation && (
              <div className="border-t border-white/15 px-3 pb-2 pt-1.5">
                <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wide text-white/70">
                  {t('chatbot.locationContact.selectLocation')}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={() => setSelectedContactLocation('kinshasa')}
                    className="rounded-lg border border-white/20 bg-white/10 px-1.5 py-1.5 text-left transition hover:border-white/40 hover:bg-white/20"
                    aria-label={t('chatbot.locationContact.openForm', { location: t('chatbot.locationContact.locations.kinshasa') })}
                  >
                    <span className="flex items-center gap-1 text-[10px] font-semibold leading-tight">
                      <MapPinIcon className="h-3 w-3 shrink-0" />
                      {t('chatbot.locationContact.locations.kinshasa')}
                    </span>
                    <span className="mt-0.5 block text-[9px] text-white/75">+243 819 935 053</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedContactLocation('lubumbashi')}
                    className="rounded-lg border border-white/20 bg-white/10 px-1.5 py-1.5 text-left transition hover:border-white/40 hover:bg-white/20"
                    aria-label={t('chatbot.locationContact.openForm', { location: t('chatbot.locationContact.locations.lubumbashi') })}
                  >
                    <span className="flex items-center gap-1 text-[10px] font-semibold leading-tight">
                      <MapPinIcon className="h-3 w-3 shrink-0" />
                      {t('chatbot.locationContact.locations.lubumbashi')}
                    </span>
                    <span className="mt-0.5 block text-[9px] text-white/75">+243 841 364 201</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {isCdWebsite && selectedContactLocation ? (
            <ChatLocationContactPanel
              location={selectedContactLocation}
              onBack={() => setSelectedContactLocation(null)}
            />
          ) : (
          <div
            data-lenis-prevent
            onWheel={(e) => e.stopPropagation()}
            className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-slate-50 p-4 [scrollbar-width:thin] [scrollbar-color:#14b8a6_#dbeafe] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-slate-200/70 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-teal-500 [&::-webkit-scrollbar-thumb:hover]:bg-teal-600"
          >
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`flex max-w-[85%] ${message.sender === 'user' ? 'ml-auto justify-end' : 'justify-start'}`}
              >
                <div
                  className={`rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                    message.sender === 'user'
                      ? 'rounded-br-md bg-teal-600 text-white'
                      : 'rounded-bl-md bg-white text-slate-700'
                  }`}
                >
                  <div>
                    {message.sender === 'bot' ? formatMessageText(message.text) : message.text}
                  </div>
                  <span className="mt-1.5 block text-[10px] opacity-70">{formatTime(message.timestamp)}</span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex max-w-[85%] justify-start">
                <div className="rounded-2xl rounded-bl-md bg-white px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.25s]"></span>
                    <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]"></span>
                    <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            
            {/* Quick Suggestions - Show when only welcome message exists */}
            {messages.length === 1 && messages[0].id === 'welcome' && !isLoading && (
              <div className="mt-2 py-2">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t('chatbot.suggestionsTitle', 'Try asking:')}</p>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 transition hover:border-teal-500 hover:bg-teal-50 hover:text-teal-700"
                    onClick={() => handleSuggestionClick(t('chatbot.suggestions.aboutUs', 'Tell me about Cool & Care'))}
                    disabled={isLoading}
                  >
                    {t('chatbot.suggestions.aboutUs', 'Tell me about Cool & Care')}
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 transition hover:border-teal-500 hover:bg-teal-50 hover:text-teal-700"
                    onClick={() => handleSuggestionClick(t('chatbot.suggestions.services', 'What services do you offer?'))}
                    disabled={isLoading}
                  >
                    {t('chatbot.suggestions.services', 'What services do you offer?')}
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 transition hover:border-teal-500 hover:bg-teal-50 hover:text-teal-700"
                    onClick={() => handleSuggestionClick(t('chatbot.suggestions.contact', 'How can I contact you?'))}
                    disabled={isLoading}
                  >
                    {t('chatbot.suggestions.contact', 'How can I contact you?')}
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 transition hover:border-teal-500 hover:bg-teal-50 hover:text-teal-700"
                    onClick={() => handleSuggestionClick(t('chatbot.suggestions.location', 'Where are you located?'))}
                    disabled={isLoading}
                  >
                    {t('chatbot.suggestions.location', 'Where are you located?')}
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {(!isCdWebsite || !selectedContactLocation) && (
          <form className="flex gap-2 border-t border-slate-200 bg-white p-3" onSubmit={handleSendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:cursor-not-allowed disabled:bg-slate-100"
              placeholder={t('chatbot.inputPlaceholder', 'Type your message...')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isLoading}
            />
            <button 
              type="submit" 
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-400"
              disabled={!inputValue.trim() || isLoading}
              aria-label={t('chatbot.sendMessage', 'Send message')}
            >
              <SendIcon className="h-4 w-4" />
            </button>
          </form>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
